import { useEnvironment } from "@keycloak/keycloak-ui-shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getLinkedAccounts, unLinkAccount } from "../../api/methods";
import { LinkedAccountRepresentation } from "../../api/representations";
import { EmptyRow } from "../../components/datalist/EmptyRow";
import { Page } from "../../components/page/Page";
import { usePromise } from "../../utils/usePromise";
import { AccountRow } from "./components/AccountRow";
import { Environment } from "../../environment";
import Card from "../../components/card/Card";
import { Box, List } from "@mui/material";
import NoLinkedAccount from "./components/NoLinkedAccount";
import { withDivider } from "../../hoc/withDivider";
import Modal from "../../components/modal/Modal";
import { useAccountAlerts } from "../../utils/useAccountAlerts";
import InfoMessage from "./components/InfoMessage";
import { cardContentStyles, cardHeaderStyles, cardStyles } from "./styles";

export const LinkedAccounts = () => {
  const { t } = useTranslation();
  const context = useEnvironment<Environment>();
  const { addAlert, addError } = useAccountAlerts();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAccount, setModalAccount] = useState<LinkedAccountRepresentation | null>(null);

  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccountRepresentation[]>([]);
  const [unlinkedAccounts, setUninkedAccounts] = useState<LinkedAccountRepresentation[]>([]);

  const { error: unlinkedError, loading: unlinkedLoading } = usePromise(
    (signal) => getLinkedAccounts({ signal, context }, { first: 0, max: 20, linked: false }),
    setUninkedAccounts,
    [context]
  );

  const { error: linkedError, loading: linkedLoading } = usePromise(
    (signal) => getLinkedAccounts({ signal, context }, { first: 0, max: 10, linked: true }),
    setLinkedAccounts,
    [context]
  );

  const handleUnlinkClick = (account: LinkedAccountRepresentation) => {
    setModalAccount(account);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalAccount(null);
  };

  const handleUnlinkConfirm = async () => {
    if (!modalAccount) return;
    try {
      await unLinkAccount(context, modalAccount);
      addAlert(t("unLinkSuccess"));
      setLinkedAccounts(prev =>
        prev.filter(acc => acc.providerName !== modalAccount.providerName)
      );
    } catch (error) {
      addError("unLinkError", error);
    }
    handleModalClose();
  };


  if (unlinkedError?.statusCode === 403 || linkedError?.statusCode === 403 || !context.environment.features.isLinkedAccountsEnabled) {
    window.location.href = context.environment.baseUrl;
    return null;
  }

  return (
    <>
      {!unlinkedLoading && !unlinkedLoading && (<Page
        title={t("linkedAccounts")}
        description={t("linkedAccountsIntroMessage")}
      >
        <Card
          title={t("linkedLoginProviders")}
          sxContent={cardContentStyles}
          sxHeader={cardHeaderStyles}
          sxCard={cardStyles}
        >
          <List id="linked-idps" aria-label={t("linkedLoginProviders")} sx={{ padding: 0 }}>
            {linkedAccounts.length > 0 ? (
              linkedAccounts.map((account, index) =>
                withDivider(AccountRow)({
                  items: linkedAccounts,
                  isLinked: true,
                  refresh: () => {},
                  account,
                  index,
                  showDivider: index < linkedAccounts.length - 1,
                  onUnlink: handleUnlinkClick,
                })
              )
            ) : (
              <NoLinkedAccount t={t} resourceUrl={context.environment.resourceUrl} />
            )}
          </List>
        </Card>
        <Card
          title={t("unlinkedLoginProviders")}
          sxContent={cardContentStyles}
          sxHeader={cardHeaderStyles}
        >
          <Box mb={1.3}>
            <InfoMessage message={t('chooseNetwork')} />
          </Box>
          <List id="unlinked-idps" aria-label={t("unlinkedLoginProviders")}>
            {unlinkedAccounts.length > 0 ? (
              unlinkedAccounts.map((account, index) =>
                withDivider(AccountRow)({
                  items: unlinkedAccounts,
                  refresh: () => {},
                  account,
                  index,
                  showDivider: index < unlinkedAccounts.length - 1,
                })
              )
            ) : (
              <EmptyRow message={t("unlinkedEmpty")} />
            )}
          </List>
        </Card>
        <Modal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          title={t("unlinkModalTitle", { providerName: modalAccount?.displayName })}
          confirmText={t("unLink")}
          onConfirm={handleUnlinkConfirm}
        >
          {t("unlinkModalDesc")}
        </Modal>
      </Page>)}
    </>
  );
};

export default LinkedAccounts;
